import type { IInstrumentTemplateQuestionSeed } from '@brushfire/types'
import type { InstrumentDraftQuestion } from '@/features/instruments/infra/instrumentDraft.api'

type OptionalSeedField =
  | 'description'
  | 'randomizeOrder'
  | 'layoutHorizontal'
  | 'imageSize'
  | 'ratingScaleMax'
  | 'ratingDisplayStyle'
  | 'ratingLowLabel'
  | 'ratingHighLabel'
  | 'likertPointCount'
  | 'likertLabels'
  | 'likertLayoutHorizontal'
  | 'sliderMinValue'
  | 'sliderMaxValue'
  | 'sliderStep'
  | 'sliderLowLabel'
  | 'sliderHighLabel'
  | 'minLength'
  | 'maxLength'
  | 'placeholder'
  | 'yesLabel'
  | 'noLabel'

const OPTIONAL_SEED_FIELDS: readonly OptionalSeedField[] = [
  'description',
  'randomizeOrder',
  'layoutHorizontal',
  'imageSize',
  'ratingScaleMax',
  'ratingDisplayStyle',
  'ratingLowLabel',
  'ratingHighLabel',
  'likertPointCount',
  'likertLabels',
  'likertLayoutHorizontal',
  'sliderMinValue',
  'sliderMaxValue',
  'sliderStep',
  'sliderLowLabel',
  'sliderHighLabel',
  'minLength',
  'maxLength',
  'placeholder',
  'yesLabel',
  'noLabel',
]

function assignOptionalSeedField<K extends OptionalSeedField>(
  row: InstrumentDraftQuestion,
  q: IInstrumentTemplateQuestionSeed,
  field: K,
): void {
  const value = q[field]
  if (value == null) {
    return
  }
  Object.assign(row, { [field]: value })
}

function mapSingleTemplateSeed(
  q: IInstrumentTemplateQuestionSeed,
  index: number,
): InstrumentDraftQuestion {
  const options = q.options?.map((opt, optIndex) => ({
    id: `template-preview-q${index}-opt${optIndex}`,
    label: opt.label,
    ...(opt.imageUrl ? { imageUrl: opt.imageUrl } : {}),
  }))

  const row: InstrumentDraftQuestion = {
    id: `template-preview-q${index}`,
    order: index,
    type: q.type as InstrumentDraftQuestion['type'],
    prompt: q.prompt,
    mandatory: q.mandatory ?? false,
  }

  for (const field of OPTIONAL_SEED_FIELDS) {
    assignOptionalSeedField(row, q, field)
  }

  if (options?.length) {
    row.options = options
  }

  return row
}

/** Maps template seed rows to draft shape so builder preview mappers can hydrate `BuilderQuestion`. */
export function mapTemplateSeedToInstrumentDraft(
  seeds: readonly IInstrumentTemplateQuestionSeed[],
): InstrumentDraftQuestion[] {
  const sorted = [...seeds].sort((a, b) => a.order - b.order)
  return sorted.map((seed, index) => mapSingleTemplateSeed(seed, index))
}
